import { useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import Modal from "components/ui/modal/Modal";
import { CustomerDocumentDetails } from "lib/hooks/api/customer_documents/useCustomerDocuments";
import { useDocumentUploadProgress } from "../../content_library/DocumentUploadProgressProvider";
import UploadCustomerDocumentsForm from "../../content_library/upload_form/UploadCustomerDocumentsForm";
import Button from "../../ui/button/Button";
import { EllipsisTooltipTextLink } from "../../ui/tooltip/EllipsisTooltip";

export const EXAMPLE_BID_REPORT_LINK =
  "https://app.stotles.com/shared/bid_reports/32c39749-0de6-44b9-aec7-542ca2c5dcdc?sig=356460b6";

type Props = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  sourceUrl: string | undefined;
  primaryButtonText?: string;
  onSubmit: (files: File[]) => void;
  /**
   * Documents already attached to the bid. Rendered as locked rows in the same
   * list as newly-added files — the user can add more but can't remove these.
   */
  existingDocuments?: CustomerDocumentDetails[];
  /** Bid id whose upload progress gates the submit button. */
  bidId?: string;
};
export function UploadTenderDocumentModal({
  isOpen,
  onOpenChange,
  sourceUrl,
  primaryButtonText = "Upload",
  onSubmit,
  existingDocuments = [],
  bidId,
}: Props) {
  const { control, watch } = useForm<{ files: File[] }>({
    defaultValues: { files: [] },
    mode: "onChange",
  });

  const { files } = watch();

  const { filesProcessing } = useDocumentUploadProgress(bidId);
  const hasAnyDocuments = existingDocuments.length > 0 || files.length > 0;

  return (
    <Modal
      open={isOpen}
      showCloseButton={false}
      onOpenChange={onOpenChange}
      padding={32}
      aria-label="Upload bid document modal"
    >
      <Flex flexDirection="column" gap={24}>
        <Flex flexDirection="column" gap={8}>
          <Text h2>Upload tender documents</Text>
          <Text color="sysTextSecondary">
            We'll use the tender document help set up for bid qualification and bid writing.
          </Text>
        </Flex>

        {sourceUrl && (
          <Flex>
            <Text color="sysTextSecondary">Access tender documents via this link </Text>
            <EllipsisTooltipTextLink
              to={sourceUrl}
              fullText={sourceUrl}
              maxWidth={300}
              targetType="new-tab"
            />
          </Flex>
        )}
        <UploadCustomerDocumentsForm
          name="files"
          control={control}
          isLoading={filesProcessing}
          maxHeight={150}
          existingDocuments={existingDocuments}
          exampleLink={
            !hasAnyDocuments
              ? {
                  url: EXAMPLE_BID_REPORT_LINK,
                  buttonText: "View an example report",
                }
              : undefined
          }
        />

        {filesProcessing && (
          <Text description color="sysTextSecondary">
            An upload is already in progress. Wait for it to finish before starting another.
          </Text>
        )}

        <ButtonContainer>
          <Button buttonType="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            loading={filesProcessing}
            disabled={!hasAnyDocuments || filesProcessing}
            onClick={() => {
              onSubmit(files);
              onOpenChange(false);
            }}
          >
            {primaryButtonText}
          </Button>
        </ButtonContainer>
      </Flex>
    </Modal>
  );
}

const ButtonContainer = styled(Flex)({
  justifyContent: "flex-end",
  gap: 16,
  marginTop: 16,
});
